<template>
  <InterfaceModalBase
    :showModal="showModal"
    @update:showModal="$emit('update:showModal', $event)"
    size-tailwind-class="sm:max-w-3xl"
    :showButtons="contentType === 'single'"
  >
    <template #heading> {{ nodeType }} </template>
    <template #title>
      <div class="flex flex-col justify-start">
      <span
        v-if="props.filterByEntityName != ''"
        class="flex text-base  text-gray-600"
        >{{ props.filterByEntityName }} {{ nodeType==nodeTypes.donor?'from':'to' }}:
        </span>
        <span class="flex">{{ entityName }}</span>
      </div>
    </template>

    <template #content>
      <div class="divider mt-4 mb-6 h-[1px] w-full bg-Neutral-400" />
      <div v-if="contentType === 'multiple'">
        <p class="text-left text-black text-sm uppercase font-semibold">
          <span v-if="props.entityName != 'Others'">Select A Recipient </span>
          <span v-else
            >Main countries with low countributions grouped in the "Others"
            category
          </span>
        </p>
        <div class="flex px-6">
          <div class="flex flex-col justify-around pb-6 pt-5">
            <div
              v-for="recipient in recipientsWithData"
              class="text-left flex-1 whitespace-nowrap"
            >
              <nuxt-link
                :to="'/recipients/' + recipient.recipient_code"
                class="link leading-3 font-bold cursor-pointer"
              >
                {{ recipient.label }}
              </nuxt-link>
            </div>
          </div>
          <chartsBarChart :data="recipientsWithData" :formater="$formater.salaryFormater"/>
        </div>
      </div>

      <!--- display info from a country -->
      <div v-if="contentType === 'single'" class="text-left">
        <div class="mb-5">
          <h3 class="mini-title">SPENDING CATEGORIES</h3>
          <aggregatedStackedHorizontal
            :series="allCategoriesReadyForStacked"
            :showLegend="false"
            :showLabels="false"
            :barHeight="60"
            :colorScaleExternal="categoryColorScale"
            rectClass="stroke-white stroke-2 hover:brightness-110"
            :roundedRadius="4"
            :axisWidth="0"
          ></aggregatedStackedHorizontal>
        </div>
        <div class="mb-5">
          <h3 class="mini-title">Top 5 categories</h3>
          <div class="flex gap-1 mb-8 flex-wrap">
            <InterfaceLabelBadge
              v-for="(
                cat, index
              ) in allCategoriesReadyForStacked[0].children.slice(0, 5)"
              :key="index"
              :category="cat.id"
              parentId="sneakPeak"
             
            />
          </div>
        </div>
        <div class="mb-5" v-if="nodeType === nodeTypes.donor">
          <h3 class="mini-title">Top recipient countries</h3>
          <div>
            <span v-for="country in recipientsWithData" class="mr-2">
              <nuxt-link
                v-if="country.recipient_code"
                :to="'/recipients/' + country.recipient_code"
                class="link"
                disabled
              >
                {{ country.label }} ({{
                  $formater.salaryFormater(country.value)
                }})
              </nuxt-link>
              <template v-else>
                {{ country.label }} ({{
                  $formater.salaryFormater(country.value)
                }})
              </template>
            </span>
          </div>
        </div>
      </div>
    </template>
    <template #buttons>
      <template v-if="contentType === 'single'">
        <nuxt-link :to="linkToUrl" class="btn primary">
          Explore {{ entityName }}
        </nuxt-link>
      </template>
    </template>
  </InterfaceModalBase>
</template>

<script setup>
import { nodeTypes } from "@/assets/configStrings.js";
import { httpConfig } from "@/assets/baseTypes.js";
import { queries } from "@/assets/queries.js";
import { categoryColorScale } from "@/assets/baseTypes.js";
import recipientsData from "@/assets/recipients.json";
import { scaleLinear, sum, descending } from "d3";

const props = defineProps({
  showModal: {
    type: Boolean,
    default: true,
  },
  contentType: {
    type: String,
    default: "single", // single vs multiple
  },
  nodeType: {
    type: String,
    default: nodeTypes.Donor,
  },
  closeAtClickOutside: {
    type: Boolean,
    default: true,
  },
  entityName: {
    type: String,
    default: "entity",
  },
  entityId: {
    type: String,
    default: "GER",
  },
  categories: {
    type: Array,
    default: () => [
      {
        name: "category1",
        value: 1,
      },
      {
        name: "category2",
        value: 2,
      },
      {
        name: "category3",
        value: 3,
      },
    ],
  },
  recipients: {
    type: Array,
    default: () => [],
  },
  filterByEntityName: {
    type: String,
    default: "",
  },
});
const emit = defineEmits(["update:showModal"]);



const config = useRuntimeConfig();
watch(
  () => props.entityName,
  (newValue, oldValue) => {
    if (props.contentType === "multiple") {
      if (props.entityName != "Others") pullDataCountriesOneRegion();
      else {
        recipientsWithData.value = props.recipients
          .map((d) => {
            return {
              label: recipientsData.find((item) => item.recipient_code == d.id)
                ?.name,
              value: d.TOTAL,
              recipient_code: d.id,
            };
          })
          .sort((a, b) => descending(a.value, b.value));
        recipientsWithData.value = recipientsWithData.value.slice(0, 10);
      }
    }
    if (props.contentType === "single" && props.nodeType === nodeTypes.donor) {
      pullRecipientsOneDonor();
    }
  }
);
const linkToUrl = computed(() => {
  if (props.nodeType === nodeTypes.donor) {
    return "/bydonors/" + props.entityId;
  }
  if (props.nodeType === nodeTypes.recipient) {
    return "/recipients/" + props.entityId;
  }
});

const recipientsWithData = ref([]);
const isLoading = ref(false);

function pullDataCountriesOneRegion() {
  // const reloadOnError = useReloadTokenOnError();

  isLoading.value = true;
  /** pull data in case this is a region **/
  const configAllRecipients = httpConfig(
    JSON.stringify(queries.getAllRecipientsValuesOneRegion(props.entityName))
  );
  
  configAllRecipients.onResponseError = ({response})=>{
    if(response.status == 401){
      emit('update:showModal', false)
      reloadOnError.refreshReload();
      //emit('update:showModal', true)
    }
  }

  $fetch(config.public.apiURL, configAllRecipients)
    .then((res) => {
      recipientsWithData.value = res.documents.map((d) => {
        return {
          label: d._id,
          value: d.TOTAL,
          recipient_code: d.recipient_code,
        };
      });
      // add code to each recipient
      recipientsWithData.value.forEach((recipient) => {
        recipient.recipient_code = props.recipients.find(
          (item) => item.name === recipient.label
        )?.recipient_code;
      });
      recipientsWithData.value.sort((a, b) => descending(a.value, b.value));
    })
    .catch((err) => {
      console.log(err.data);
      // if err.data= ''
      // reloadOnError.refreshReload();
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function pullRecipientsOneDonor() {
  isLoading.value = true;
  /** pull data in case this is a region **/
  const configAllRecipients = httpConfig(
    JSON.stringify(queries.getAllReceipientsOneDonor(props.entityName))
  );
  $fetch(config.public.apiURL, configAllRecipients)
    .then((res) => {
      recipientsWithData.value = res.documents.map((d) => {
        return {
          label: d._id,
          value: d.Total,
        };
      });
      // add code to each recipient
      recipientsWithData.value.forEach((recipient) => {
        recipient.recipient_code = recipientsData.find(
          (item) => item.name === recipient.label
        )?.recipient_code;
      });
      recipientsWithData.value.sort((a, b) => descending(a.value, b.value));
      recipientsWithData.value = recipientsWithData.value.slice(0, 10);
    })
    .catch((err) => {
      console.log(err);
    })
    .finally(() => {
      isLoading.value = false;
    });
}
const categoriesWithRelative = computed(() => {
  const total = sum(props.categories, (d) => d.value);
  return props.categories
    .map((d) => {
      return {
        ...d,
        valueRelative: d.value / total,
      };
    })
    .sort((a, b) => b.value - a.value);
});

const allCategoriesReadyForStacked = computed(() => {
  return [
    {
      id: "",
      value: sum(props.categories, (d) => d.value),
      children: categoriesWithRelative.value,
    },
  ];
});
</script>

<style lang="scss" scoped></style>
